/* Hexdeck – Riftbound Deck Builder.
   Card data is rendered by Shopify (templates/page.cards.liquid) and loaded as JSON.
   Decks are stored in localStorage; available cards are added to the Shopify cart
   through the Ajax Cart API with a hidden "_deck" line property. */
(() => {
  const root = document.querySelector('[data-deck-builder]');
  if (!root) return;

  const H = window.Hexdeck || {};
  const $ = (s, r = root) => r.querySelector(s);
  const $$ = (s, r = root) => Array.from(r.querySelectorAll(s));
  const S = JSON.parse($('[data-db-strings]').textContent);
  const cfg = {
    url: root.dataset.url,
    maxCopies: parseInt(root.dataset.maxCopies, 10) || 3,
    mainSize: parseInt(root.dataset.mainSize, 10) || 40,
    runeSize: parseInt(root.dataset.runeSize, 10) || 12,
    battlefields: parseInt(root.dataset.battlefields, 10) || 3,
    lowStock: parseInt(root.dataset.lowStock, 10) || 3
  };
  // Standard ban list (theme setting "Banned cards"), matched on card name with any kind of dash.
  const banKey = (s) => String(s ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[‐-―-]/g, ' ').replace(/[^a-z0-9 ]/g, '').replace(/\s+/g, ' ').trim();
  const BANNED = new Set((root.dataset.banned || '').split('|').map(banKey).filter(Boolean));
  const STORAGE_KEY = 'hexdeck.decks.v1';
  const PAGE_SIZE = 48;

  /* ---------------------------------------------------------------- helpers */
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  // Locale strings use %name% placeholders so Liquid never interpolates them.
  const fmt = (s, vars = {}) => String(s).replace(/%(\w+)%/g, (_, k) => (vars[k] !== undefined ? vars[k] : ''));
  const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
  const uid = () => Math.random().toString(36).slice(2, 10);
  const norm = (s) => String(s ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

  const moneyFormat = (() => {
    const t = document.createElement('textarea');
    t.innerHTML = H.moneyFormat || '€{{amount_with_comma_separator}}';
    return t.value;
  })();
  function money(cents) {
    if (cents === null || cents === undefined || Number.isNaN(cents)) return '';
    const value = cents / 100;
    const num = (n, dec, th, d) => {
      const [i, f] = n.toFixed(dec).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, th) + (f ? d + f : '');
    };
    return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => {
      switch (k) {
        case 'amount_with_comma_separator': return num(value, 2, '.', ',');
        case 'amount_no_decimals': return num(value, 0, ',', '');
        case 'amount_no_decimals_with_comma_separator': return num(value, 0, '.', '');
        case 'amount_with_apostrophe_separator': return num(value, 2, "'", '.');
        case 'amount_with_space_separator': return num(value, 2, ' ', ',');
        default: return num(value, 2, ',', '.');
      }
    });
  }

  const live = (msg) => { const el = $('[data-db-live]'); if (el) { el.textContent = ''; requestAnimationFrame(() => { el.textContent = msg; }); } };
  const toast = (msg, type, link) => (H.toast ? H.toast(msg, type, link) : live(msg));

  function typeKey(t) {
    const v = norm(t);
    if (!v) return 'other';
    if (v.includes('legend')) return 'legend';
    if (v.includes('battlefield')) return 'battlefield';
    if (v.includes('rune')) return 'rune';
    if (v.includes('champion')) return 'champion';
    if (v.includes('unit')) return 'unit';
    if (v.includes('spell')) return 'spell';
    if (v.includes('gear')) return 'gear';
    return 'other';
  }
  const TYPE_ORDER = ['legend', 'champion', 'unit', 'spell', 'gear', 'other', 'rune', 'battlefield'];
  const typeLabel = (key, sample) => (key === 'other' ? S.other : (sample || key));

  /* Availability of a card for a wanted quantity. */
  function availability(card, wanted = 1) {
    const p = card.p;
    if (!p || !p.v) return { key: 'none', label: S.notSold, units: 0 };
    if (p.tba) return { key: 'soon', label: S.comingSoon, units: 0 };
    if (!p.a) return { key: 'out', label: S.notInStock, units: 0 };
    if (p.po) return { key: 'pre', label: S.preorder, units: wanted };
    if (p.q === null || p.q === undefined) return { key: 'in', label: S.inStock, units: wanted };
    const units = Math.min(wanted, p.q);
    if (p.q < wanted) return { key: 'low', label: fmt(S.lowStock, { count: p.q }), units };
    if (p.q <= cfg.lowStock) return { key: 'low', label: fmt(S.lowStock, { count: p.q }), units };
    return { key: 'in', label: S.inStock, units };
  }

  const bigImage = (url) => (url ? url.replace(/([?&])width=\d+/, '$1width=900').replace(/([?&])w=\d+/, '$1w=744') : '');

  /* ------------------------------------------------------------------ state */
  const state = {
    cards: [],
    byId: new Map(),
    filtered: [],
    rendered: 0,
    f: { q: '', s: '', t: '', r: '', d: '', c: '', rg: '', stock: false },
    sort: 'set',
    store: { current: null, decks: {} }
  };

  function loadStore() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) state.store = JSON.parse(raw);
    } catch (_) { /* storage unavailable */ }
    if (!state.store.decks || !Object.keys(state.store.decks).length) {
      const id = uid();
      state.store = { current: id, decks: { [id]: { id, name: S.defaultName, cards: {}, updated: Date.now() } } };
    }
    // Tidy up: drop untouched empty decks (default name, no cards) except the one in use.
    Object.values(state.store.decks).forEach((x) => {
      if (x.id !== state.store.current && isBlank(x) && Object.keys(state.store.decks).length > 1) delete state.store.decks[x.id];
    });
    if (!state.store.decks[state.store.current]) state.store.current = Object.keys(state.store.decks)[0];
  }
  function isBlank(x) { return !Object.keys(x.cards || {}).length && (!x.name || x.name === S.defaultName); }
  // A new deck is only created when there is no empty deck to reuse, so empty decks never pile up.
  function newDeck(name, cards) {
    // Reuse the open deck when it is untouched, otherwise any other untouched "New deck".
    const cur = state.store.decks[state.store.current];
    const empty = cur && isBlank(cur) ? cur : Object.values(state.store.decks).find(isBlank);
    const d = empty || { id: uid() };
    Object.assign(d, { name: name || S.defaultName, cards: cards || {}, updated: Date.now() });
    state.store.decks[d.id] = d;
    state.store.current = d.id;
    return d;
  }
  const saveLocal = debounce(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state.store)); } catch (_) { /* ignore */ }
  }, 200);
  // Logged-in customers: decks (incl. the "Want list") are also saved on their account through
  // /apps/binder/decks (Shopify app proxy → binder service → customer metafield hexdeck.decks).
  // Only when the theme setting "Save decks to customer accounts" is on (needs the binder service's /decks route).
  const customerId = root.dataset.deckSync === 'true' ? root.dataset.customer : '';
  const syncAccount = debounce(() => {
    if (!customerId) return;
    fetch('/apps/binder/decks', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(state.store) })
      .catch(() => { /* offline: the next change tries again */ });
  }, 1500);
  const saveStore = () => { saveLocal(); syncAccount(); };
  // On load: combine this device's decks with the account's; per deck the most recently changed version wins.
  function mergeAccountDecks() {
    const el = $('[data-db-account-decks]');
    if (!customerId || !el) return;
    let saved = null;
    try { saved = JSON.parse(el.textContent); } catch (_) { /* nothing saved yet */ }
    const savedDecks = (saved && saved.decks) || {};
    let changed = false;
    Object.values(savedDecks).forEach((d) => {
      const local = state.store.decks[d.id];
      if (!local || (d.updated || 0) > (local.updated || 0)) { state.store.decks[d.id] = d; changed = true; }
    });
    Object.values(state.store.decks).forEach((x) => { if (isBlank(x) && Object.keys(state.store.decks).length > 1 && x.id !== state.store.current) delete state.store.decks[x.id]; });
    if (!state.store.decks[state.store.current]) state.store.current = (saved && savedDecks[saved.current]) ? saved.current : Object.keys(state.store.decks)[0];
    const differs = Object.keys(state.store.decks).some((id) => !savedDecks[id] || JSON.stringify(savedDecks[id].cards) !== JSON.stringify(state.store.decks[id].cards));
    if (changed) saveLocal();
    if (differs) syncAccount();
  }
  const deck = () => state.store.decks[state.store.current];

  /* ------------------------------------------------------------------- data */
  async function fetchPage(n) {
    // No JSON Accept header: Shopify would answer with its own page JSON instead of our template.
    const res = await fetch(`${cfg.url}&page=${n}`, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const text = await res.text();
    const start = text.indexOf('{');
    const end = text.lastIndexOf('}');
    return JSON.parse(text.slice(start, end + 1));
  }

  async function load() {
    $('[data-db-error]').hidden = true;
    $('[data-db-grid]').setAttribute('aria-busy', 'true');
    try {
      const first = await fetchPage(1);
      const rest = [];
      if (first.pages > 1) {
        const pages = [];
        for (let i = 2; i <= first.pages; i += 1) pages.push(fetchPage(i));
        rest.push(...(await Promise.all(pages)));
      }
      let cards = [];
      if (first.source === 'catalog') {
        // Official card catalogue (theme asset) + Shopify price/stock keyed by card code.
        const products = {};
        [first, ...rest].forEach((r) => Object.entries(r.products || {}).forEach(([k, v]) => { products[norm(k)] = v; }));
        const catalogue = await fetch(root.dataset.catalogUrl).then((r) => {
          if (!r.ok) throw new Error(`Catalogue HTTP ${r.status}`);
          return r.json();
        });
        cards = (catalogue.cards || []).map((c) => ({
          ...c,
          n: c.sub ? `${c.n} – ${c.sub}` : c.n,
          img: c.img ? `${c.img}?w=300&fm=webp&q=80` : '',
          p: products[norm(c.id)] || null
        }));
      } else {
        cards = [first, ...rest].reduce((all, r) => all.concat(r.cards || []), []);
      }
      state.cards = cards.filter((c) => c && c.id).map((c, i) => {
        const card = { ...c, id: String(c.id), i, tk: typeKey(c.t), d: Array.isArray(c.d) ? c.d : (c.d ? [c.d] : []) };
        card.blob = norm([card.n, card.fn, card.id, card.s, card.c, card.t, card.rg, card.r, card.d.join(' '), (card.tg || []).join(' '), card.a].join(' '));
        card.price = card.p && card.p.pr !== undefined ? card.p.pr : null;
        card.ban = BANNED.size > 0 && (BANNED.has(banKey(card.n)) || BANNED.has(banKey(card.fn)));
        return card;
      });
      state.byId = new Map(state.cards.map((c) => [norm(c.id), c]));
      buildFilterOptions();
      applyDeepLinks();
      renderMeta();
      applyFilters();
      renderDeck();
    } catch (err) {
      $('[data-db-grid]').innerHTML = '';
      $('[data-db-error]').hidden = false;
      console.error('[Hexdeck deck builder]', err);
    } finally {
      $('[data-db-grid]').setAttribute('aria-busy', 'false');
    }
  }

  function buildFilterOptions() {
    $$('[data-db-filter]').forEach((sel) => {
      const key = sel.dataset.dbFilter;
      const values = new Set();
      state.cards.forEach((c) => {
        const v = c[key];
        if (Array.isArray(v)) v.forEach((x) => x && values.add(x));
        else if (v) values.add(v);
      });
      const sorted = Array.from(values).sort((a, b) => String(a).localeCompare(String(b)));
      sorted.forEach((v) => {
        const o = document.createElement('option');
        o.value = v;
        o.textContent = v;
        sel.appendChild(o);
      });
      sel.hidden = sorted.length < 2;
      const label = root.querySelector(`label[for="${sel.id}"]`);
      if (label) label.hidden = sel.hidden;
    });
  }

  /* ------------------------------------------------------------- filtering */
  function applyFilters() {
    const { f } = state;
    const q = norm(f.q).trim();
    const terms = q ? q.split(/\s+/) : [];
    let list = state.cards.filter((c) => {
      if (terms.length && !terms.every((t) => c.blob.includes(t))) return false;
      if (f.s && c.s !== f.s) return false;
      if (f.t && c.t !== f.t) return false;
      if (f.r && c.r !== f.r) return false;
      if (f.c && c.c !== f.c) return false;
      if (f.rg && c.rg !== f.rg) return false;
      if (f.d && !c.d.includes(f.d)) return false;
      if (f.stock) {
        const a = availability(c);
        if (a.key === 'none' || a.key === 'out' || a.key === 'soon') return false;
      }
      return true;
    });
    const byNum = (a, b) => (a ?? Infinity) - (b ?? Infinity);
    switch (state.sort) {
      case 'name': list.sort((a, b) => String(a.n).localeCompare(String(b.n))); break;
      case 'energy': list.sort((a, b) => byNum(a.e, b.e) || String(a.n).localeCompare(String(b.n))); break;
      case 'price-asc': list.sort((a, b) => byNum(a.price, b.price)); break;
      case 'price-desc': list.sort((a, b) => (b.price ?? -1) - (a.price ?? -1)); break;
      default: list.sort((a, b) => a.i - b.i);
    }
    state.filtered = list;
    state.rendered = 0;
    $('[data-db-grid]').innerHTML = '';
    renderMore();
    const active = Object.entries(f).some(([k, v]) => (k === 'stock' ? v : v !== ''));
    $('[data-db-reset]').hidden = !active;
    $('[data-db-count]').textContent = fmt(S.cardsShown, { count: list.length, total: state.cards.length });
    $('[data-db-empty]').hidden = list.length > 0;
    root.classList.toggle('is-catalog-empty', state.cards.length === 0);
  }

  function tileHTML(c) {
    const qty = deck().cards[c.id] || 0;
    const a = availability(c);
    const img = c.img
      ? `<img src="${esc(c.img)}" alt="" loading="lazy" decoding="async" width="240" height="335">`
      : `<span class="db-noimg"><span>${esc(c.n)}</span></span>`;
    return `
      <div class="db-tile${c.o === 'l' ? ' db-tile--landscape' : ''}${qty ? ' is-in-deck' : ''}${a.key === 'none' || a.key === 'out' ? ' is-unavailable' : ''}" data-id="${esc(c.id)}">
        <button type="button" class="db-tile__img" data-open aria-label="${esc(fmt(S.enlarge, { name: c.n }))}">${img}</button>
        <span class="db-tile__qty" ${qty ? '' : 'hidden'}>${qty}×</span>
        ${c.ban ? `<span class="db-ban">${esc(S.banned)}</span>` : ''}
        <div class="db-tile__info">
          <span class="db-tile__name">${esc(c.n)}</span>
          <span class="db-tile__meta">
            <span class="db-tile__id">${esc(c.id)}</span>
            ${a.key === 'none'
    ? ''
    : a.key === 'soon' ? `<span class="db-tile__stock db-tile__stock--soon">${esc(a.label)}</span>`
    : `<span class="db-tile__stock db-tile__stock--${a.key}"><span class="db-dot db-dot--${a.key}"></span>${c.price !== null && a.key !== 'out' ? esc(money(c.price)) : esc(a.label)}</span>`}
          </span>
        </div>
        <div class="db-tile__actions">
          <button type="button" class="db-btn" data-dec aria-label="${esc(S.removeFromDeck)}: ${esc(c.n)}" ${qty ? '' : 'disabled'}><svg class="icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14"/></svg></button>
          <button type="button" class="db-btn db-btn--add" data-inc aria-label="${esc(S.addToDeck)}: ${esc(c.n)}"><svg class="icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg></button>
        </div>
      </div>`;
  }

  function renderMore() {
    const grid = $('[data-db-grid]');
    const next = state.filtered.slice(state.rendered, state.rendered + PAGE_SIZE);
    if (!next.length) return;
    grid.insertAdjacentHTML('beforeend', next.map(tileHTML).join(''));
    state.rendered += next.length;
  }

  function refreshTile(id) {
    const el = root.querySelector(`.db-tile[data-id="${CSS.escape(id)}"]`);
    const c = state.byId.get(norm(id));
    if (!el || !c) return;
    const qty = deck().cards[c.id] || 0;
    el.classList.toggle('is-in-deck', qty > 0);
    const badge = $('.db-tile__qty', el);
    badge.hidden = !qty;
    badge.textContent = `${qty}×`;
    $('[data-dec]', el).disabled = !qty;
  }

  /* ---------------------------------------------------------------- deck ops */
  function limitFor(c) {
    // "Order extra copies" lifts the deck-building limit (e.g. for playsets or collecting).
    if (state.store.allowExtra) return 99;
    return c.tk === 'rune' ? cfg.runeSize : cfg.maxCopies;
  }

  function setQty(id, qty, silent) {
    const c = state.byId.get(norm(id));
    const d = deck();
    if (!c) {
      // Card no longer in the catalogue: allow removing it from the deck.
      if (qty < (d.cards[id] || 0)) {
        if (qty <= 0) delete d.cards[id]; else d.cards[id] = qty;
        saveStore();
        renderDeck();
      }
      return;
    }
    const max = limitFor(c);
    let n = Math.max(0, qty);
    if (n > max) {
      n = max;
      toast(fmt(S.maxCopies, { count: max }), 'error');
    }
    if (n === 0) delete d.cards[c.id];
    else d.cards[c.id] = n;
    d.updated = Date.now();
    saveStore();
    refreshTile(c.id);
    renderDeck();
    if (!silent) live(`${c.n}: ${n} ${S.inDeck}`);
    const modal = $('[data-db-modal]');
    if (modal.open && modal.dataset.id === c.id) renderModal(c);
  }
  const change = (id, delta) => {
    const c = state.byId.get(norm(id));
    const key = c ? c.id : id;
    setQty(key, (deck().cards[key] || 0) + delta);
  };

  function deckLines() {
    const d = deck();
    return Object.entries(d.cards)
      .map(([id, qty]) => ({ card: state.byId.get(norm(id)) || { id, n: id, tk: 'other', missing: true }, qty }))
      .filter((l) => l.qty > 0);
  }

  function renderDeck() {
    const d = deck();
    $('[data-db-name]').value = d.name;

    // Saved decks select
    const sel = $('[data-db-decks]');
    sel.innerHTML = Object.values(state.store.decks)
      .sort((a, b) => b.updated - a.updated)
      .map((x) => `<option value="${esc(x.id)}" ${x.id === d.id ? 'selected' : ''}>${esc(x.name)} (${Object.values(x.cards).reduce((s, n) => s + n, 0)})</option>`)
      .join('');

    const lines = deckLines();
    const list = $('[data-db-list]');
    const counts = { legend: 0, main: 0, rune: 0, battlefield: 0 };
    let totalUnits = 0;
    let availUnits = 0;
    let availValue = 0;
    const cartItems = [];

    const groups = {};
    lines.forEach((l) => {
      const k = l.card.tk;
      (groups[k] = groups[k] || []).push(l);
      totalUnits += l.qty;
      if (k === 'legend') counts.legend += l.qty;
      else if (k === 'rune') counts.rune += l.qty;
      else if (k === 'battlefield') counts.battlefield += l.qty;
      else counts.main += l.qty;
      const a = l.card.missing ? { key: 'none', label: S.notSold, units: 0 } : availability(l.card, l.qty);
      l.a = a;
      if (a.units > 0) {
        availUnits += a.units;
        availValue += a.units * (l.card.p.pr || 0);
        const item = { id: l.card.p.v, quantity: a.units, properties: { _deck: d.name } };
        if (l.card.p.po) item.properties['Pre-order'] = S.preorder;
        cartItems.push({ item, card: l.card });
      }
    });

    if (!lines.length) {
      list.innerHTML = list.dataset.emptyHtml || '';
    } else {
      list.innerHTML = TYPE_ORDER.filter((k) => groups[k]).map((k) => {
        const g = groups[k].sort((a, b) => (a.card.e ?? 99) - (b.card.e ?? 99) || String(a.card.n).localeCompare(String(b.card.n)));
        const sum = g.reduce((s, l) => s + l.qty, 0);
        return `
          <section class="db-group">
            <h3 class="db-group__title"><span>${esc(typeLabel(k, g[0].card.t))}</span><span>${sum}</span></h3>
            <ul role="list">${g.map((l) => lineHTML(l)).join('')}</ul>
          </section>`;
      }).join('');
    }

    // Stats
    const stat = (label, n, target) => {
      const ok = target ? n === target : true;
      const over = target && n > target;
      return `<div class="db-stat${target ? (ok ? ' is-ok' : over ? ' is-over' : '') : ''}"><b>${n}${target ? `<small>/${target}</small>` : ''}</b><span>${esc(label)}</span></div>`;
    };
    $('[data-db-stats]').innerHTML = [
      stat(S.legend, counts.legend, 1),
      stat(S.main, counts.main, cfg.mainSize),
      cfg.runeSize ? stat(S.runes, counts.rune, cfg.runeSize) : '',
      cfg.battlefields ? stat(S.battlefields, counts.battlefield, cfg.battlefields) : ''
    ].join('');

    // Banned cards in this deck (Standard ban list)
    const banned = lines.filter((l) => l.card.ban).map((l) => l.card.n);
    const warn = $('[data-db-banwarn]');
    if (warn) {
      warn.hidden = !banned.length;
      warn.textContent = banned.length ? fmt(S.bannedWarn, { cards: banned.join(', ') }) : '';
    }

    // Summary + CTA
    const missing = totalUnits - availUnits;
    $('[data-db-summary]').innerHTML = totalUnits ? `
      <div class="cart-summary__row"><span>${esc(S.available)}</span><span><b>${availUnits}</b> / ${totalUnits}</span></div>
      ${missing > 0 ? `<div class="cart-summary__row db-summary__missing"><span>${esc(S.missing)}</span><span>${missing}</span></div>` : ''}
      <div class="cart-summary__row cart-summary__total"><span>${esc(S.deckValue)}</span><span>${esc(money(availValue))}</span></div>` : '';

    const staffRun = $('[data-db-staff-run]');
    if (staffRun) {
      staffRun.disabled = !totalUnits;
      staffRun.textContent = totalUnits ? `Update stock: ${totalUnits} cards` : 'Update stock';
    }

    const btn = $('[data-db-add-all]');
    btn.disabled = cartItems.length === 0;
    $('[data-db-add-all-label]').textContent = cartItems.length
      ? fmt(S.addAllCount, { count: availUnits, price: money(availValue) })
      : (totalUnits ? S.nothingAvailable : S.addAll);
    state.cartItems = cartItems;

    // Mobile bar
    $('[data-db-bar-count]').textContent = totalUnits;
    $('[data-db-bar-sub]').textContent = totalUnits ? `${availUnits} ${S.available.toLowerCase()} · ${money(availValue)}` : S.empty;
  }

  function lineHTML(l) {
    const c = l.card;
    const a = l.a || { key: 'none', label: S.notSold };
    const price = c.p && c.p.pr !== undefined ? money(c.p.pr * l.qty) : '';
    return `
      <li class="db-line db-line--${a.key}" data-id="${esc(c.id)}">
        <button type="button" class="db-line__thumb" data-open aria-label="${esc(fmt(S.enlarge, { name: c.n }))}">${c.img ? `<img src="${esc(c.img)}" alt="" loading="lazy" width="40" height="56">` : ''}</button>
        <div class="db-line__main">
          <span class="db-line__name">${esc(c.n)}</span>
          <span class="db-line__stock">${c.ban ? `<span class="db-ban db-ban--inline">${esc(S.banned)}</span>` : ''}<span class="db-dot db-dot--${a.key}"></span>${esc(a.label)}</span>
        </div>
        <div class="db-line__qty">
          <button type="button" class="db-btn db-btn--sm" data-dec aria-label="${esc(S.removeFromDeck)}: ${esc(c.n)}"><svg class="icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14"/></svg></button>
          <span aria-label="${l.qty}">${l.qty}</span>
          <button type="button" class="db-btn db-btn--sm" data-inc aria-label="${esc(S.addToDeck)}: ${esc(c.n)}"><svg class="icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg></button>
        </div>
        <span class="db-line__price">${esc(price)}</span>
      </li>`;
  }

  /* ------------------------------------------------------------------ modal */
  function renderModal(c) {
    const modal = $('[data-db-modal]');
    modal.dataset.id = c.id;
    const qty = deck().cards[c.id] || 0;
    const a = availability(c);
    const rows = [
      [S.cardId, c.id], [S.set, c.s], [S.rarity, c.r], [S.type, c.t], [S.domain, c.d.join(', ')],
      [S.champion, c.c], [S.region, c.rg], [S.energy, c.e], [S.might, c.m], [S.power, c.pw], [S.artist, c.a]
    ].filter(([, v]) => v !== null && v !== undefined && v !== '');
    $('[data-db-modal-body]').innerHTML = `
      <div class="db-modal__img">${c.img ? `<img src="${esc(bigImage(c.img))}" alt="${esc(c.n)}">` : `<span class="db-noimg db-noimg--lg"><span>${esc(c.n)}</span></span>`}</div>
      <div class="db-modal__info">
        ${c.r ? `<span class="rarity" data-rarity="${esc(norm(c.r).replace(/\s+/g, '-'))}">${esc(c.r)}</span>` : ''}
        <h2 id="DbModalTitle" class="h3">${esc(c.n)}</h2>
        ${c.x ? `<p class="db-modal__text" data-card-text>${esc(c.x)}</p>` : ''}
        <table class="spec-table"><tbody>${rows.map(([k, v]) => `<tr><th scope="row">${esc(k)}</th><td>${esc(v)}</td></tr>`).join('')}</tbody></table>
        <div class="db-modal__buy">
          <div class="buybox__stock"><span class="stock stock--${a.key === 'pre' || a.key === 'soon' ? 'preorder' : a.key === 'none' || a.key === 'out' ? 'out' : a.key}">${esc(a.label)}</span>${c.price !== null && a.key !== 'soon' ? `<span class="price">${esc(money(c.price))}</span>` : ''}</div>
          <div class="db-modal__row">
            <div class="qty" aria-label="${esc(S.inDeck)}">
              <button type="button" data-dec aria-label="${esc(S.removeFromDeck)}" ${qty ? '' : 'disabled'}><svg class="icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14"/></svg></button>
              <input type="text" readonly value="${qty}" aria-label="${esc(S.inDeck)}" tabindex="-1">
              <button type="button" data-inc aria-label="${esc(S.addToDeck)}"><svg class="icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg></button>
            </div>
            <span class="muted small">${esc(S.inDeck)}</span>
          </div>
          <div class="cluster">
            ${c.p && c.p.a ? `<button type="button" class="btn btn--primary" data-db-cart-one>${esc(S.addToCart)}</button>` : ''}
            ${c.p && c.p.u ? `<a class="btn btn--secondary" href="${esc(c.p.u)}">${esc(S.viewProduct)}</a>` : ''}
          </div>
        </div>
      </div>`;
    const text = $('[data-card-text]', $('[data-db-modal-body]'));
    if (text && H.cardText) H.cardText.render(text);
  }

  function openModal(id) {
    const c = state.byId.get(norm(id));
    if (!c) return;
    renderModal(c);
    const modal = $('[data-db-modal]');
    if (!modal.open) {
      if (H.openDialog) {
        modal.id = modal.id || 'DbModal';
        H.openDialog(modal.id, document.activeElement);
      } else modal.showModal();
    }
  }

  /* ------------------------------------------------------------------ cart */
  async function addAllToCart() {
    const items = state.cartItems || [];
    if (!items.length || !H.cart) return;
    const btn = $('[data-db-add-all]');
    btn.classList.add('is-loading');
    let added = 0;
    let lastError = '';
    const failed = [];
    try {
      await H.cart.add(items.map((x) => x.item));
      added = items.reduce((s, x) => s + x.item.quantity, 0);
    } catch (err) {
      // One line failed (e.g. stock changed) – retry one by one to add what we can.
      for (const x of items) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await H.cart.add([x.item]);
          added += x.item.quantity;
        } catch (e) {
          lastError = e.message;
          failed.push(x.card.n);
        }
      }
    } finally {
      btn.classList.remove('is-loading');
    }
    if (added && !failed.length) {
      toast(fmt(S.added, { count: added }), 'success');
      H.cart.open();
    } else if (added) {
      toast(fmt(S.addedPartial, { count: added, cards: failed.join(', ') }), 'error');
      H.cart.open();
    } else {
      toast(lastError || S.addFailed, 'error');
    }
  }

  async function addOneToCart(c, btn) {
    if (!H.cart || !c.p) return;
    btn.classList.add('is-loading');
    try {
      const item = { id: c.p.v, quantity: 1, properties: { _deck: deck().name } };
      await H.cart.add([item]);
      $('[data-db-modal]').close();
      H.cart.open();
    } catch (err) {
      toast(err.message || S.addFailed, 'error');
    } finally {
      btn.classList.remove('is-loading');
    }
  }

  /* ------------------------------------------------------- import / export */
  function exportText() {
    const d = deck();
    const lines = deckLines();
    const byType = {};
    lines.forEach((l) => { (byType[l.card.tk] = byType[l.card.tk] || []).push(l); });
    return [d.name, ''].concat(
      TYPE_ORDER.filter((k) => byType[k]).flatMap((k) => [
        `# ${typeLabel(k, byType[k][0].card.t)}`,
        ...byType[k].map((l) => `${l.qty} ${l.card.n}${l.card.id && l.card.id !== l.card.n ? ` (${l.card.id})` : ''}`),
        ''
      ])
    ).join('\n').trim();
  }

  // Card names as other sites write them: "Jinx, Loose Cannon" / "Jinx - Loose Cannon" / "Jinx – Loose Cannon".
  const nameKey = (s) => norm(s).replace(/[’`]/g, "'").replace(/\s*[-–—,:]\s*/g, ' ').replace(/\s+/g, ' ').trim();
  let nameIndex = null;
  function findCard(token) {
    const t = norm(token).trim();
    if (!t) return null;
    // A card number anywhere wins: "(OGN-030)", "[OGN-030]", "OGN-030a"
    const code = t.match(/\b([a-z]{2,4}-\d{2,3}[a-z]?)\b/);
    if (code && state.byId.get(code[1])) return state.byId.get(code[1]);
    if (state.byId.get(t)) return state.byId.get(t);
    if (!nameIndex) {
      // Catalogue order puts the regular printing before alt-arts, so the first match is the normal card.
      nameIndex = new Map();
      state.cards.forEach((c) => [c.fn, c.n].forEach((n) => { if (n && !nameIndex.has(nameKey(n))) nameIndex.set(nameKey(n), c); }));
    }
    const name = nameKey(t.replace(/\s*[([][^)\]]*[)\]]\s*$/, ''));
    return nameIndex.get(name) || null;
  }

  // Reads deck lists as people paste them from deck sites or exports:
  //   "3 Card", "3x Card", "Card x3", "x3 Card", optional card numbers, section headers
  //   ("Legend:", "Main Deck (40)", "Runes", ...) and "Legend: Jinx, Loose Cannon". Sideboards are skipped.
  function parseList(text) {
    const cards = {};
    const missing = [];
    let name = null;
    let skip = false;
    const header = /^(legend|champions?|main\s*deck|deck|runes?|battlefields?|side\s*board|units?|spells?|gear|signature)\b[^0-9]*?(\(\d+\))?\s*:?\s*$/i;
    String(text).split(/\r?\n/).forEach((raw, idx) => {
      let line = raw.replace(/\s+\/\/.*$/, '').replace(/\t/g, ' ').trim();
      if (!line || line.startsWith('#') || line.startsWith('//')) return;
      const inline = line.match(/^(legend|champion|battlefield)s?\s*:\s*(\S.*)$/i);
      if (inline) line = /^\d/.test(inline[2]) ? inline[2] : `1 ${inline[2]}`;
      else if (header.test(line) || /:\s*$/.test(line)) { skip = /side\s*board|maybe/i.test(line); return; }
      let m = line.match(/^(\d+)\s*[x×]?\s+(.+)$/i) || line.match(/^[x×]\s*(\d+)\s+(.+)$/i);
      if (!m) { const s = line.match(/^(.+?)\s+[x×]\s*(\d+)$/i); if (s) m = [line, s[2], s[1]]; }
      if (!m) {
        if (idx === 0 && !name) name = line;
        return;
      }
      if (skip) return;
      const c = findCard(m[2]);
      if (c) cards[c.id] = Math.min((cards[c.id] || 0) + parseInt(m[1], 10), limitFor(c));
      else missing.push(m[2]);
    });
    return { name, cards, missing };
  }

  function importText(text, asNew, deckName) {
    const parsed = parseList(text);
    const cards = parsed.cards;
    const missing = parsed.missing;
    const name = deckName || parsed.name;
    if (asNew) {
      newDeck(name, cards);
    } else {
      const d = deck();
      d.cards = cards;
      if (name) d.name = name;
      d.updated = Date.now();
    }
    saveStore();
    renderDeck();
    applyFilters();
    return { count: Object.values(cards).reduce((s, n) => s + n, 0), missing };
  }

  /* ------------------------------------------------------------ meta decks */
  // Recommended decks from the "meta_deck" metaobjects. Each tile shows the legend, how much of the
  // deck is in stock and what that costs; one click loads it as a deck (reusing an empty one).
  let metaDecks = [];
  try { metaDecks = JSON.parse(($('[data-db-meta]') || {}).textContent || '[]'); } catch (_) { metaDecks = []; }
  metaDecks.sort((a, b) => (a.p ?? 999) - (b.p ?? 999));

  function renderMeta() {
    const row = $('[data-db-meta-row]');
    if (!row || !metaDecks.length) return;
    row.innerHTML = metaDecks.map((m, i) => {
      const { cards } = parseList(m.x);
      const entries = Object.entries(cards).map(([id, q]) => [state.byId.get(norm(id)), q]).filter(([c]) => c);
      const legend = (m.l && findCard(m.l)) || (entries.find(([c]) => c.tk === 'legend') || [])[0];
      let total = 0;
      let avail = 0;
      let cost = 0;
      entries.forEach(([c, q]) => {
        total += q;
        const a = availability(c, q);
        avail += a.units;
        cost += a.units * ((c.p && c.p.pr) || 0);
      });
      const img = legend && legend.img ? `<img src="${esc(legend.img)}" alt="" loading="lazy" width="240" height="335">` : '<span class="db-noimg"></span>';
      return `
        <article class="db-meta__deck" role="listitem">
          <button type="button" class="db-meta__load" data-meta="${i}" aria-label="${esc(S.metaLoad)}: ${esc(m.n)}">
            <span class="db-meta__art">${img}</span>
            <span class="db-meta__info">
              ${m.t ? `<span class="db-meta__tag">${esc(m.t)}</span>` : ''}
              <strong class="db-meta__name">${esc(m.n)}</strong>
              <span class="db-meta__stock">${esc(fmt(S.metaStock, { available: avail, total }))}${cost ? ` · ${esc(money(cost))}` : ''}</span>
              <span class="db-meta__cta">${esc(S.metaLoad)} →</span>
            </span>
          </button>
          ${m.u ? `<a class="db-meta__source" href="${esc(m.u)}" target="_blank" rel="noopener">${esc(S.metaSource)}</a>` : ''}
        </article>`;
    }).join('');
  }

  const metaRow = $('[data-db-meta-row]');
  if (metaRow) {
    metaRow.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-meta]');
      if (!btn || !state.cards.length) return;
      const m = metaDecks[Number(btn.dataset.meta)];
      const res = importText(m.x, true, m.n);
      toast(fmt(S.metaLoaded, { name: m.n, count: res.count }), 'success');
      if (window.matchMedia('(max-width: 1099px)').matches) $('[data-db-open-deck]').click();
      else $('[data-db-deck]').scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }
  const pasteBtn = $('[data-db-paste-open]');
  if (pasteBtn) pasteBtn.addEventListener('click', () => $('[data-db-import-open]').click());

  const b64 = {
    enc: (s) => btoa(unescape(encodeURIComponent(s))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''),
    dec: (s) => decodeURIComponent(escape(atob(s.replace(/-/g, '+').replace(/_/g, '/'))))
  };

  function shareUrl() {
    const d = deck();
    const payload = `${d.name}|${Object.entries(d.cards).map(([id, n]) => `${id}:${n}`).join(',')}`;
    const url = new URL(window.location.href);
    url.search = '';
    url.hash = `deck=${b64.enc(payload)}`;
    return url.toString();
  }

  function applyDeepLinks() {
    const hash = window.location.hash.match(/deck=([\w-]+)/);
    if (hash) {
      try {
        const [name, list] = b64.dec(hash[1]).split('|');
        const cards = {};
        (list || '').split(',').forEach((pair) => {
          const [id, n] = pair.split(':');
          const c = state.byId.get(norm(id));
          if (c && parseInt(n, 10) > 0) cards[c.id] = Math.min(parseInt(n, 10), limitFor(c));
        });
        newDeck(name, cards);
        saveStore();
        history.replaceState(null, '', window.location.pathname + window.location.search);
      } catch (_) { /* invalid share link */ }
    }
    const params = new URLSearchParams(window.location.search);
    const cardParam = params.get('card');
    if (cardParam) setTimeout(() => openModal(cardParam), 50);
  }

  async function copy(text) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (_) {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      ta.remove();
      return ok;
    }
  }

  /* ---------------------------------------------------------------- events */
  const onSearch = debounce((v) => { state.f.q = v; applyFilters(); }, 150);
  $('[data-db-search]').addEventListener('input', (e) => onSearch(e.target.value));
  $$('[data-db-filter]').forEach((sel) => sel.addEventListener('change', () => { state.f[sel.dataset.dbFilter] = sel.value; applyFilters(); }));
  $('[data-db-sort]').addEventListener('change', (e) => { state.sort = e.target.value; applyFilters(); });
  $('[data-db-instock]').addEventListener('change', (e) => { state.f.stock = e.target.checked; applyFilters(); });
  $('[data-db-reset]').addEventListener('click', () => {
    state.f = { q: '', s: '', t: '', r: '', d: '', c: '', rg: '', stock: false };
    $('[data-db-search]').value = '';
    $$('[data-db-filter]').forEach((s) => { s.value = ''; });
    $('[data-db-instock]').checked = false;
    applyFilters();
  });
  $('[data-db-retry]').addEventListener('click', load);

  root.addEventListener('click', (e) => {
    const holder = e.target.closest('[data-id]');
    const id = holder && holder.dataset.id;
    if (e.target.closest('[data-inc]') && id) { change(id, 1); return; }
    if (e.target.closest('[data-dec]') && id) { change(id, -1); return; }
    if (e.target.closest('[data-open]') && id) { openModal(id); return; }
    const one = e.target.closest('[data-db-cart-one]');
    if (one) { const c = state.byId.get(norm($('[data-db-modal]').dataset.id)); if (c) addOneToCart(c, one); }
  });

  $('[data-db-add-all]').addEventListener('click', addAllToCart);

  /* ------------------------------------------------- staff: deck → stock */
  // Only rendered for customers tagged "hexdeck-staff". The binder service checks the tag and the scanner PIN
  // again and changes stock with the same logic as the scanner (never below 0).
  const staff = $('[data-db-staff]');
  if (staff) {
    const pinInput = $('[data-db-staff-pin]', staff);
    const result = $('[data-db-staff-result]', staff);
    try { pinInput.value = sessionStorage.getItem('hexdeck.staffPin') || ''; } catch (e) { /* private mode */ }
    $('[data-db-staff-run]', staff).addEventListener('click', async (e) => {
      const btn = e.currentTarget;
      const remove = $('[data-db-staff-mode]', staff).value === 'remove';
      const foil = $('[data-db-staff-finish]', staff).value === 'foil';
      const pin = pinInput.value.trim();
      if (!pin) { result.textContent = 'Enter the scanner PIN first.'; pinInput.focus(); return; }
      const changes = [];
      const skipped = [];
      deckLines().forEach((l) => {
        const p = l.card.p;
        const variantId = p && (foil ? p.vf : (p.vn || p.v));
        if (!variantId) { skipped.push(l.card.n); return; }
        changes.push({ variantId: String(variantId), delta: remove ? -l.qty : l.qty, card: l.card });
      });
      if (!changes.length) { result.textContent = `None of these cards exist in the shop${foil ? ' as foil' : ''}.`; return; }
      btn.disabled = true;
      result.textContent = 'Updating stock…';
      try {
        const res = await fetch('/apps/binder/stock', {
          method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pin, key: `deck-${uid()}-${Date.now()}`, changes: changes.map(({ variantId, delta }) => ({ variantId, delta })) })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.error) {
          result.textContent = data.error === 'wrong_pin' ? 'Wrong PIN.' : data.error === 'not_staff' ? 'This account is not tagged hexdeck-staff.' : `Failed: ${data.error || res.status}`;
          return;
        }
        try { sessionStorage.setItem('hexdeck.staffPin', pin); } catch (err) { /* private mode */ }
        const moved = (data.results || []).reduce((s, r) => s + Math.abs(r.delta), 0);
        // Update the stock shown on the page without reloading.
        (data.results || []).forEach((r) => {
          const c = changes.find((x) => String(x.variantId) === String(r.variantId));
          if (c && c.card.p && !foil && c.card.p.q !== null && c.card.p.q !== undefined) {
            c.card.p.q = r.after;
            c.card.p.a = r.after > 0 && !c.card.p.tba;
            const tile = root.querySelector(`.db-tile[data-id="${CSS.escape(c.card.id)}"]`);
            if (tile) tile.outerHTML = tileHTML(c.card);
          }
        });
        renderDeck();
        result.textContent = `${remove ? 'Removed' : 'Added'} ${moved} cards (${(data.results || []).length} products)${foil ? ' – foil' : ''}.`
          + (skipped.length ? ` Not in the shop: ${skipped.join(', ')}.` : '')
          + ((data.errors || []).length ? ` Problems: ${data.errors.join('; ')}` : '');
        toast(result.textContent, 'success');
      } catch (err) {
        result.textContent = `Failed: ${err.message}`;
      } finally {
        btn.disabled = false;
      }
    });
  }

  $('[data-db-name]').addEventListener('input', debounce((e) => {
    deck().name = e.target.value.trim() || S.defaultName;
    deck().updated = Date.now();
    saveStore();
    renderDeck();
  }, 300));

  $('[data-db-decks]').addEventListener('change', (e) => {
    state.store.current = e.target.value;
    saveStore();
    renderDeck();
    applyFilters();
  });

  const extraToggle = $('[data-db-extra]');
  if (extraToggle) {
    extraToggle.checked = !!state.store.allowExtra;
    extraToggle.addEventListener('change', () => {
      state.store.allowExtra = extraToggle.checked;
      saveStore();
      if (!state.store.allowExtra) {
        // Bring quantities back within the deck limit.
        Object.keys(deck().cards).forEach((id) => {
          const c = state.byId.get(norm(id));
          if (c && deck().cards[id] > limitFor(c)) deck().cards[id] = limitFor(c);
        });
        saveStore();
      }
      renderDeck();
      applyFilters();
      live(extraToggle.checked ? S.extraOn : S.extraOff);
    });
  }

  $('[data-db-new]').addEventListener('click', () => {
    newDeck();
    saveStore();
    renderDeck();
    applyFilters();
    $('[data-db-name]').select();
  });

  // Delete the current deck (with undo). The last deck is emptied instead of removed.
  $('[data-db-delete]').addEventListener('click', () => {
    const d = deck();
    if (Object.keys(state.store.decks).length === 1 && isBlank(d)) return;
    const backup = JSON.parse(JSON.stringify(d));
    delete state.store.decks[d.id];
    const rest = Object.values(state.store.decks).sort((a, b) => b.updated - a.updated);
    if (rest.length) state.store.current = rest[0].id; else newDeck();
    saveStore();
    renderDeck();
    applyFilters();
    const region = document.querySelector('[data-toast-region]');
    if (region) {
      const el = document.createElement('div');
      el.className = 'toast';
      el.innerHTML = `<span>${esc(S.deckDeleted)}</span><a href="#" role="button">Undo</a>`;
      el.querySelector('a').addEventListener('click', (ev) => {
        ev.preventDefault();
        Object.values(state.store.decks).forEach((x) => { if (isBlank(x)) delete state.store.decks[x.id]; });
        state.store.decks[backup.id] = backup;
        state.store.current = backup.id;
        saveStore();
        renderDeck();
        applyFilters();
        el.remove();
      });
      region.appendChild(el);
      setTimeout(() => el.remove(), 6000);
    }
  });

  $('[data-db-clear]').addEventListener('click', () => {
    const d = deck();
    const backup = { ...d.cards };
    if (!Object.keys(backup).length) return;
    d.cards = {};
    saveStore();
    renderDeck();
    applyFilters();
    // Undo instead of a blocking confirm dialog.
    const region = document.querySelector('[data-toast-region]');
    if (region) {
      const el = document.createElement('div');
      el.className = 'toast';
      el.innerHTML = `<span>${esc(S.cleared)}</span><a href="#" role="button">Undo</a>`;
      el.querySelector('a').addEventListener('click', (ev) => {
        ev.preventDefault();
        d.cards = backup;
        saveStore();
        renderDeck();
        applyFilters();
        el.remove();
      });
      region.appendChild(el);
      setTimeout(() => el.remove(), 6000);
    }
  });

  $('[data-db-share]').addEventListener('click', async () => { if (await copy(shareUrl())) toast(S.shareCopied, 'success'); });
  $('[data-db-export]').addEventListener('click', async () => { if (await copy(exportText())) toast(S.copied, 'success'); });

  const importDialog = $('[data-db-import]');
  importDialog.id = importDialog.id || 'DbImport';
  $('[data-db-import-open]').addEventListener('click', (e) => {
    $('[data-db-import-result]').textContent = '';
    if (H.openDialog) H.openDialog(importDialog.id, e.currentTarget); else importDialog.showModal();
  });
  $('[data-db-import-run]').addEventListener('click', () => {
    const res = importText($('[data-db-import-text]').value, true);
    const out = $('[data-db-import-result]');
    out.textContent = fmt(S.importDone, { count: res.count }) + (res.missing.length ? ` ${fmt(S.importMissing, { cards: res.missing.slice(0, 8).join(', ') })}` : '');
    if (!res.missing.length) setTimeout(() => importDialog.close(), 700);
  });

  // Mobile deck sheet
  $('[data-db-open-deck]').addEventListener('click', () => {
    root.classList.add('is-deck-open');
    document.body.classList.add('is-locked');
    $('[data-db-close-deck]').focus({ preventScroll: true });
  });
  $('[data-db-close-deck]').addEventListener('click', () => {
    root.classList.remove('is-deck-open');
    document.body.classList.remove('is-locked');
    $('[data-db-open-deck]').focus();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && root.classList.contains('is-deck-open')) $('[data-db-close-deck]').click();
  });

  // Infinite render
  if ('IntersectionObserver' in window) {
    new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && state.rendered < state.filtered.length) renderMore();
    }, { rootMargin: '800px 0px' }).observe($('[data-db-sentinel]'));
  }

  // Keep the empty deck markup for re-use
  const list = $('[data-db-list]');
  list.dataset.emptyHtml = list.innerHTML;

  // Sync decks across tabs
  window.addEventListener('storage', (e) => {
    if (e.key !== STORAGE_KEY) return;
    loadStore();
    renderDeck();
    applyFilters();
  });

  loadStore();
  mergeAccountDecks();
  // /pages/deck-builder#wants opens the want list (filled from product pages)
  if (window.location.hash === '#wants' && state.store.decks.wants) state.store.current = 'wants';
  renderDeck();
  load();
})();
